import { AxiosRequestConfig } from 'axios';
import { nanoid as uuid } from 'nanoid';

import * as api from 'utils/api';

import {
  SearchArgs,
  SearchHeaders,
  SearchQuery,
  PaymentInstrumentService as PaymentInstrumentServiceInterface,
  GetByIdArgs,
  GetByIdHeaders,
  CreateArgs,
  CreateHeaders,
  RequestHeader
} from './interfaces';
import { InstrumentRecord, SearchResponse } from './types';
import { isNullOrUndefined, removeNulls } from './utils';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const url = require('url');

class RequiredError extends Error {
  name: 'RequiredError' = 'RequiredError';
}

export default class PaymentInstrumentService implements PaymentInstrumentServiceInterface {
  private adapter: api.Adapter;

  constructor(options: AxiosRequestConfig = {}) {
    this.adapter = new api.Adapter(options);
  }

  async getById(args: GetByIdArgs) {
    const { accessToken, instrumentId, customerId, xTransactionID = uuid() } = args;

    // Argument validation
    if (isNullOrUndefined(accessToken)) {
      throw new RequiredError('accessToken is required when calling getById.');
    }
    if (isNullOrUndefined(customerId)) {
      throw new RequiredError('customerId is required when calling getById.');
    }
    if (isNullOrUndefined(instrumentId)) {
      throw new RequiredError('instrumentId is required when calling getById.');
    }

    const headers: RequestHeader<GetByIdHeaders> = {
      Authorization: `Bearer ${accessToken}`,
      'X-TransactionID': xTransactionID ?? null,
      'X-CustomerId': customerId ?? null
    };

    const address = `/instruments/${instrumentId}`;
    const config = { headers: removeNulls(headers) };
    const response = await this.adapter.getValue<InstrumentRecord>(address, config);
    return response.data;
  }

  async search(args: SearchArgs) {
    const {
      accessToken,
      customerId,
      xTransactionID = uuid(),
      expiryStatus,
      type,
      page,
      pageSize
    } = args;

    if (isNullOrUndefined(accessToken)) {
      throw new RequiredError('accessToken is required when calling search.');
    }
    if (isNullOrUndefined(customerId)) {
      throw new Error('customerId is required when calling search.');
    }

    const headers: RequestHeader<SearchHeaders> = {
      Authorization: `Bearer ${accessToken}`,
      'X-TransactionID': xTransactionID ?? null
    };

    const query: RequestHeader<SearchQuery> = {
      customerId,
      expiryStatus: (expiryStatus as string) ?? null,
      type: type ?? null,
      page: page ?? null,
      pageSize: pageSize ?? null
    };

    const urlObject = url.parse('/instruments', true);
    urlObject.query = { ...removeNulls(query) };
    const config = { headers: removeNulls(headers) };

    const response = await this.adapter.getValue<SearchResponse>(url.format(urlObject), config);
    return response.data;
  }

  async create(args: CreateArgs) {
    const {
      accessToken,
      customerId,
      instrumentRecord,
      transactionId = uuid(),
      gatewayCustomerId
    } = args;

    if (isNullOrUndefined(accessToken)) {
      throw new RequiredError('accessToken is required when calling create.');
    }
    if (isNullOrUndefined(customerId)) {
      throw new Error('customerId is required when calling create.');
    }
    if (isNullOrUndefined(instrumentRecord)) {
      throw new Error('instrumentRecord is required when calling create.');
    }

    const headers: RequestHeader<CreateHeaders> = {
      Authorization: `Bearer ${accessToken}`,
      'X-TransactionID': transactionId ?? null,
      'X-CustomerId': customerId ?? null,
      'X-Gateway-CustomerId': gatewayCustomerId ?? null
    };
    const config = { headers: removeNulls(headers) };
    const response = await this.adapter.postValue<InstrumentRecord>(
      '/instruments',
      instrumentRecord,
      config
    );

    return response.data;
  }
}
